import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { CollisionDetection, DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { FilterList, Link as LinkIcon, Undo } from '@mui/icons-material'
import {
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import {
  type ChapterSectionDependency,
  type DependencyKind,
  findViolations,
  partitionDepsByAliveness,
} from '@shared/chapterSectionDependencies'
import { type Chapter, chapterSectionKey, flattenChaptersToSectionKeys } from '@shared/chapterSectionsOrder'
import type { TagDefinition } from '@shared/tags/tagRegistry'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import DroppableContainer from './DroppableContainer'
import OrganizeChapterRow from './OrganizeChapterRow'
import OrganizeSectionRow from './OrganizeSectionRow'
import SortableChapter from './SortableChapter'
import SortableSectionRow from './SortableSectionRow'
import { useChapterSectionDependenciesState } from './useChapterSectionDependenciesState'
import { useOrganizeChaptersState } from './useOrganizeChaptersState'
import SearchPreviewPanel from '../../../../components/search/SearchPreviewPanel'
import { SectionTagEditor } from '../../../../components/tags/SectionTagEditor'
import { SectionTagRail } from '../../../../components/tags/SectionTagRail'
import TagManagerDialog from '../../../../components/tags/TagManagerDialog'
import { useSectionTagsEditing } from '../../../../components/tags/useSectionTagsEditing'
import { Book, ContentFile } from '../../../../services/api'
import { logger } from '../../../../utils/logger'
import type { ChapterGroup, ChapterSectionEntry } from '../../hooks/useChapterSectionsBook'
import { useSectionPreview } from '../../hooks/useSectionPreview'
import AddDependencyDialog from '../dependencies/AddDependencyDialog'
import DependencyChips from '../dependencies/DependencyChips'
import { SectionRatingFilterPanel, useSectionRatingFilter } from '../sectionRatingFilter'

const UNATTACHED_ID = '__unattached__'

/**
 * A chapter is both a sortable (its own id) and a drop zone for sections. Those
 * must be separate droppable ids: section drags exclude chapter sortables, so a
 * zone sharing the chapter's id would never be a target — which is exactly what
 * made an empty chapter impossible to drop into.
 */
const CHAPTER_ZONE_PREFIX = 'zone:'
const chapterZoneId = (chapterId: string) => `${CHAPTER_ZONE_PREFIX}${chapterId}`
const isDropZoneId = (id: string) => id === UNATTACHED_ID || id.startsWith(CHAPTER_ZONE_PREFIX)
const containerIdFromZone = (zoneId: string) =>
  zoneId.startsWith(CHAPTER_ZONE_PREFIX) ? zoneId.slice(CHAPTER_ZONE_PREFIX.length) : zoneId

interface OrganizeChaptersDialogProps {
  open: boolean
  onClose: () => void
  chapterGroups: ChapterGroup[]
  entryByKey: Map<string, ChapterSectionEntry>
  onSave: (nextDefs: Chapter[], nextDeps: ChapterSectionDependency[]) => Promise<void>
  initialDeps?: ChapterSectionDependency[]
  /** ChapterSection key (`filename:headingBlockId`) to pre-select when the dialog opens. */
  initialSelectedKey?: string | null
  bookId?: string
  book?: Book | null
  bookFontFamily?: string
  bookDirection?: 'ltr' | 'rtl'
  tagRegistry?: Record<string, TagDefinition>
  tagColorMap?: Record<string, string>
  /** Called after a successful section-tag persist (for parent refetch). */
  onSectionTagsPersisted?: () => void
  onRegistrySaved?: (tags: Record<string, TagDefinition>) => void
  /**
   * Called after the section preview editor saves an edit. The host should refresh
   * its currentChapter state if it has this source file open.
   */
  onSectionContentSaved?: (filename: string, updatedFile: ContentFile) => void
}

export default function OrganizeChaptersDialog({
  open,
  onClose,
  chapterGroups,
  entryByKey,
  onSave,
  initialDeps = [],
  initialSelectedKey,
  bookId,
  book,
  bookFontFamily,
  bookDirection = 'ltr',
  tagRegistry,
  tagColorMap,
  onSectionTagsPersisted,
  onRegistrySaved,
  onSectionContentSaved,
}: OrganizeChaptersDialogProps) {
  const unattachedGroup = chapterGroups.find((g) => g.chapter.id === '__unattached__')
  const chapterGroupsOnly = chapterGroups.filter((g) => g.chapter.id !== '__unattached__')

  const initialChapters = chapterGroupsOnly.map((g) => g.chapter)
  const initialUnattached = unattachedGroup?.chapter.chapterSections ?? []

  const {
    draftChapters,
    draftUnattached,
    selectedKeys,
    dirty: chaptersDirty,
    canUndo,
    reorderChapters,
    toggleSelection,
    clearSelection,
    findContainer,
    getDraggedKeys,
    handleDragOver,
    handleDragEnd,
    undo,
  } = useOrganizeChaptersState(initialChapters, initialUnattached, initialSelectedKey)

  const chapterIdSet = useMemo(() => new Set(draftChapters.map((ch) => ch.id)), [draftChapters])

  const {
    draftDeps,
    pickedFromKey,
    dirty: depsDirty,
    pickFrom,
    cancelFromPick,
    upsertDep,
    removeDep,
  } = useChapterSectionDependenciesState(initialDeps)

  const dirty = chaptersDirty || depsDirty

  const [mode, setMode] = useState<'reorder' | 'deps'>('reorder')
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)
  const [pendingDepTarget, setPendingDepTarget] = useState<{
    fromKey: string
    toKey: string
    initialReason: string
    initialKind?: DependencyKind
  } | null>(null)

  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const draggedKeysRef = useRef<string[]>([])

  const { effectiveTagIds, changeTagIds, resetOverrides } = useSectionTagsEditing({
    bookId,
    entryByKey,
    onPersisted: onSectionTagsPersisted,
  })

  const [tagPopover, setTagPopover] = useState<{ key: string; top: number; left: number } | null>(null)
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null)
  const [pickerSectionKey, setPickerSectionKey] = useState<string | null>(null)

  const allEntries = useMemo(() => Array.from(entryByKey.values()), [entryByKey])

  const filterApi = useSectionRatingFilter({
    entries: allEntries,
    tagRegistry,
    effectiveTagIds,
  })

  const clearFilter = filterApi.clear
  useEffect(() => {
    if (!open) {
      resetOverrides()
      setTagPopover(null)
      setFilterAnchor(null)
      setPickerSectionKey(null)
      clearFilter()
    }
  }, [open, resetOverrides, clearFilter])

  // When the dialog opens with a pre-selected section, scroll its row into view
  // inside whichever scroll container holds it (chapters area or unattached column).
  // Wait one frame so the dialog's full-screen layout has actually rendered.
  useEffect(() => {
    if (!open || !initialSelectedKey) {
      return
    }
    const raf = window.requestAnimationFrame(() => {
      const el = document.querySelector(`[data-section-key="${initialSelectedKey}"]`)
      if (el && 'scrollIntoView' in el) {
        ;(el as HTMLElement).scrollIntoView({ block: 'center', behavior: 'auto' })
      }
    })
    return () => window.cancelAnimationFrame(raf)
  }, [open, initialSelectedKey])

  const {
    previewKey,
    previewAst,
    previewLoading,
    previewError,
    previewTitle,
    previewOpen,
    previewDirty,
    previewSaving,
    previewRecentlySaved,
    openPreview,
    closePreview,
    onPreviewChange,
    onPreviewSave,
  } = useSectionPreview({ open, bookId, entryByKey, onSectionContentSaved })

  // MouseSensor (not PointerSensor) so touch is handled exclusively by TouchSensor —
  // otherwise both arm on touch and race, with PointerSensor's distance-based activation
  // sometimes winning over TouchSensor's long-press delay and breaking mobile drag.
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 5 },
    }),
    // 300ms long-press matches the editor sidebar TOC — see ChapterSectionsToc.
    useSensor(TouchSensor, {
      activationConstraint: { delay: 300, tolerance: 5 },
    }),
  )

  // Use refs to avoid stale closures in handleSave
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave
  const draftChaptersRef = useRef(draftChapters)
  draftChaptersRef.current = draftChapters
  const draftDepsRef = useRef(draftDeps)
  draftDepsRef.current = draftDeps

  // --- Dependency derived state ---

  const flatOrder = useMemo(
    () => [...flattenChaptersToSectionKeys(draftChapters), ...draftUnattached],
    [draftChapters, draftUnattached],
  )

  const canonicalKeysSet = useMemo(() => new Set(entryByKey.keys()), [entryByKey])

  const { alive: aliveDeps, orphaned: orphanedDeps } = useMemo(
    () => partitionDepsByAliveness(draftDeps, canonicalKeysSet),
    [draftDeps, canonicalKeysSet],
  )

  const violations = useMemo(() => findViolations(aliveDeps, flatOrder), [aliveDeps, flatOrder])
  const violationIds = useMemo(() => new Set(violations.map((d) => d.id)), [violations])
  const violationKeys = useMemo(() => {
    const out = new Set<string>()
    for (const v of violations) {
      out.add(v.fromKey)
      out.add(v.toKey)
    }
    return out
  }, [violations])

  const depsByKey = useMemo(() => {
    const outgoing = new Map<string, ChapterSectionDependency[]>()
    const incoming = new Map<string, ChapterSectionDependency[]>()
    for (const d of aliveDeps) {
      const out = outgoing.get(d.fromKey) ?? []
      out.push(d)
      outgoing.set(d.fromKey, out)
      const inc = incoming.get(d.toKey) ?? []
      inc.push(d)
      incoming.set(d.toKey, inc)
    }
    return { outgoing, incoming }
  }, [aliveDeps])

  /** Map of section keys related to the currently hovered key — for visual highlighting. */
  const focusRelationMap = useMemo(() => {
    if (!hoveredKey) {
      return new Map<string, 'before' | 'after'>()
    }
    const map = new Map<string, 'before' | 'after'>()
    for (const d of aliveDeps) {
      if (d.toKey === hoveredKey) {
        // d.fromKey must come BEFORE the hovered section.
        if (!map.has(d.fromKey)) {
          map.set(d.fromKey, 'before')
        }
      } else if (d.fromKey === hoveredKey) {
        // d.toKey must come AFTER the hovered section.
        if (!map.has(d.toKey)) {
          map.set(d.toKey, 'after')
        }
      }
    }
    return map
  }, [hoveredKey, aliveDeps])

  const labelByKey = useCallback(
    (key: string): string => {
      const entry = entryByKey.get(key)
      if (!entry) {
        return key
      }
      const breadcrumb = [entry.chapter.title, ...entry.ancestorLabels].map((s) => s.trim()).filter(Boolean)
      return breadcrumb.length > 0 ? `${entry.displayText} (${breadcrumb.join(' · ')})` : entry.displayText
    },
    [entryByKey],
  )

  /**
   * Options for the dependency-edit dialog's section pickers, ordered by reader flow
   * and grouped by chapter so the dropdown isn't a random hash-order soup.
   */
  const depSectionOptions = useMemo(() => {
    const out: { key: string; label: string; groupTitle?: string }[] = []
    for (const group of chapterGroups) {
      const isUnattached = group.chapter.id === '__unattached__'
      const groupTitle = isUnattached ? 'לא משויך' : group.chapter.title || 'ללא כותרת'
      for (const entry of group.entries) {
        const key = chapterSectionKey(entry.chapter.filename, entry.headingBlockId)
        out.push({ key, label: entry.displayText, groupTitle })
      }
    }
    return out
  }, [chapterGroups])

  const handleSave = useCallback(async () => {
    if (violations.length > 0) {
      const msg = `${violations.length} תלויות מופרות בסדר הנוכחי. לשמור בכל זאת?`
      if (!window.confirm(msg)) {
        return
      }
    }
    const defs = draftChaptersRef.current.map((ch) => ({
      id: ch.id,
      title: ch.title,
      chapterSections: ch.chapterSections,
    }))
    await onSaveRef.current(defs, draftDepsRef.current)
    onClose()
  }, [onClose, violations.length])

  const handleClose = useCallback(() => {
    if (previewDirty && !window.confirm('Discard unsaved changes to the section?')) {
      return
    }
    if (dirty && !window.confirm('Discard unsaved changes?')) {
      return
    }
    onClose()
  }, [dirty, previewDirty, onClose])

  // Reset deps-mode selection when leaving the mode or closing the dialog.
  useEffect(() => {
    if (mode !== 'deps' || !open) {
      cancelFromPick()
      setPendingDepTarget(null)
    } else {
      clearSelection()
    }
  }, [mode, open, cancelFromPick, clearSelection])

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) {
      return
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // When the preview dialog is open, let its own onClose handle Escape.
        if (previewKey) {
          return
        }
        if (pendingDepTarget) {
          return
        }
        if (pickedFromKey) {
          e.stopPropagation()
          cancelFromPick()
          return
        }
        if (selectedKeys.size > 0) {
          e.stopPropagation()
          clearSelection()
        }
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault()
        undo()
      }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [open, selectedKeys, previewKey, pickedFromKey, pendingDepTarget, cancelFromPick, clearSelection, undo])

  const multipleChapters = draftChapters.length > 1

  /** In deps mode, clicking a section either picks it as `from` or, if a `from` is already picked, opens the reason dialog. */
  const handleSectionClickForDeps = useCallback(
    (key: string) => {
      if (!pickedFromKey) {
        pickFrom(key)
        return
      }
      if (pickedFromKey === key) {
        cancelFromPick()
        return
      }
      const existing = draftDepsRef.current.find((d) => d.fromKey === pickedFromKey && d.toKey === key)
      setPendingDepTarget({
        fromKey: pickedFromKey,
        toKey: key,
        initialReason: existing?.reason ?? '',
        initialKind: existing?.kind,
      })
    },
    [pickedFromKey, pickFrom, cancelFromPick],
  )

  const handleDepDialogSave = useCallback(
    (fromKey: string, toKey: string, reason: string, kind: DependencyKind) => {
      upsertDep(fromKey, toKey, reason, kind)
      setPendingDepTarget(null)
      cancelFromPick()
    },
    [upsertDep, cancelFromPick],
  )

  const handleDepDialogDelete = useCallback(
    (depId: string) => {
      removeDep(depId)
      setPendingDepTarget(null)
      cancelFromPick()
    },
    [removeDep, cancelFromPick],
  )

  /** Re-open the reason dialog for an existing dep. */
  const handleEditExistingDep = useCallback((dep: ChapterSectionDependency) => {
    setPendingDepTarget({
      fromKey: dep.fromKey,
      toKey: dep.toKey,
      initialReason: dep.reason,
    })
  }, [])

  // --- DnD handlers ---

  // Keep chapter and section drags from interfering: a chapter only ever lands on
  // another chapter. A section drag resolves in two steps — the pointer picks the
  // container (so an empty chapter, which has no rows to collide with, is a valid
  // target), then the closest row inside that container picks the position.
  const detectCollisions: CollisionDetection = useCallback(
    (args) => {
      const activeId = args.active.id as string

      if (chapterIdSet.has(activeId)) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((c) => chapterIdSet.has(c.id as string)),
        })
      }

      const zones = args.droppableContainers.filter((c) => isDropZoneId(c.id as string))
      const rows = args.droppableContainers.filter(
        (c) => !isDropZoneId(c.id as string) && !chapterIdSet.has(c.id as string),
      )

      // Fall back to the nearest zone when the pointer is between containers (or
      // absent, as with the keyboard sensor).
      const zoneHit =
        pointerWithin({ ...args, droppableContainers: zones })[0] ??
        closestCenter({ ...args, droppableContainers: zones })[0]
      if (!zoneHit) {
        return closestCenter({ ...args, droppableContainers: rows })
      }

      const containerId = containerIdFromZone(zoneHit.id as string)
      const rowsInZone = rows.filter((c) => findContainer(c.id as string) === containerId)
      if (rowsInZone.length === 0) {
        return [zoneHit]
      }
      return closestCenter({ ...args, droppableContainers: rowsInZone })
    },
    [chapterIdSet, findContainer],
  )

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeId = event.active.id as string
      setActiveDragId(activeId)

      if (chapterIdSet.has(activeId)) {
        draggedKeysRef.current = []
        return
      }

      // If dragging an unselected item, clear selection and just drag that one
      if (!selectedKeys.has(activeId)) {
        clearSelection()
        draggedKeysRef.current = [activeId]
      } else {
        draggedKeysRef.current = getDraggedKeys(activeId)
      }
    },
    [chapterIdSet, selectedKeys, clearSelection, getDraggedKeys],
  )

  const onDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event
      if (!over) {
        return
      }
      const activeId = active.id as string
      if (chapterIdSet.has(activeId)) {
        return
      }
      const overId = over.id as string

      const overContainer = isDropZoneId(overId) ? containerIdFromZone(overId) : (findContainer(overId) ?? overId)
      const activeContainer = findContainer(activeId)

      if (!activeContainer || activeContainer === overContainer) {
        return
      }

      handleDragOver(activeId, overId, overContainer)
    },
    [chapterIdSet, findContainer, handleDragOver],
  )

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setActiveDragId(null)
      draggedKeysRef.current = []

      if (!over) {
        return
      }

      const activeId = active.id as string
      const overId = over.id as string

      if (chapterIdSet.has(activeId)) {
        if (chapterIdSet.has(overId)) {
          reorderChapters(activeId, overId)
        }
        return
      }

      const activeContainer = findContainer(activeId)
      const overContainer = isDropZoneId(overId) ? containerIdFromZone(overId) : findContainer(overId)

      if (activeContainer && activeContainer === overContainer && activeId !== overId) {
        handleDragEnd(activeId, overId)
      }
    },
    [chapterIdSet, reorderChapters, findContainer, handleDragEnd],
  )

  // Build the drag overlay content
  const isDraggingChapter = activeDragId !== null && chapterIdSet.has(activeDragId)
  const activeChapter = isDraggingChapter ? (draftChapters.find((ch) => ch.id === activeDragId) ?? null) : null
  const activeDragEntry = activeDragId && !isDraggingChapter ? entryByKey.get(activeDragId) : null
  const dragCount = draggedKeysRef.current.length
  const draggedSet = new Set(activeDragId ? draggedKeysRef.current : [])

  const renderSectionList = (sectionKeys: string[]) =>
    sectionKeys.map((sectionKey) => {
      const entry = entryByKey.get(sectionKey)
      if (!entry) {
        return null
      }
      const ancestorPath = [entry.chapter.title, ...entry.ancestorLabels]
        .map((s) => s.trim())
        .filter(Boolean)
        .join(' \u00b7 ')

      // Hide other dragged items (they travel with the active one)
      const isDraggedAway = activeDragId !== null && sectionKey !== activeDragId && draggedSet.has(sectionKey)

      const openTagEditorAt = (ev: React.MouseEvent) => {
        setTagPopover({ key: sectionKey, top: ev.clientY, left: ev.clientX })
      }

      const tagRail = tagRegistry ? (
        <SectionTagRail
          tagIds={effectiveTagIds(sectionKey)}
          tagRegistry={tagRegistry}
          tagColorMap={tagColorMap}
          onRequestEdit={openTagEditorAt}
        />
      ) : undefined

      const isDimmed = filterApi.isActive && !filterApi.matches(entry)

      const outgoing = depsByKey.outgoing.get(sectionKey) ?? []
      const incoming = depsByKey.incoming.get(sectionKey) ?? []
      const dependencyChips =
        outgoing.length === 0 && incoming.length === 0 ? undefined : (
          <DependencyChips
            outgoing={outgoing}
            incoming={incoming}
            violations={violationIds}
            bookDirection={bookDirection}
            bookFontFamily={bookFontFamily}
            labelByKey={labelByKey}
            onRemove={removeDep}
            onEdit={handleEditExistingDep}
          />
        )

      const isPickedFrom = mode === 'deps' && pickedFromKey === sectionKey
      const hasViolation = violationKeys.has(sectionKey)
      const relationToFocus = focusRelationMap.get(sectionKey)

      const handleSelect = (key: string, shiftKey: boolean, metaKey: boolean) => {
        if (mode === 'deps') {
          handleSectionClickForDeps(key)
          return
        }
        toggleSelection(key, shiftKey, metaKey)
      }

      return (
        <SortableSectionRow
          key={sectionKey}
          sectionKey={sectionKey}
          displayText={entry.displayText}
          ancestorPath={ancestorPath}
          isSelected={selectedKeys.has(sectionKey)}
          isDraggedAway={isDraggedAway}
          isDimmed={isDimmed}
          isPickedFrom={isPickedFrom}
          hasViolation={hasViolation}
          relationToFocus={relationToFocus}
          disableDrag={mode === 'deps'}
          onSelect={handleSelect}
          onPreview={openPreview}
          onHoverChange={setHoveredKey}
          bookFontFamily={bookFontFamily}
          bookDirection={bookDirection}
          tagRail={tagRail}
          dependencyChips={dependencyChips}
        />
      )
    })

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        PaperProps={{
          sx: { bgcolor: 'background.default' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Box sx={{ flex: 1 }}>Organize Chapters</Box>
          {mode === 'deps' && pickedFromKey && (
            <Typography sx={{ fontSize: '0.8rem', color: 'primary.main' }}>
              בחר את המקטע שצריך להגיע אחרי (ESC לביטול)
            </Typography>
          )}
          {mode === 'reorder' && selectedKeys.size > 0 && (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{selectedKeys.size} selected</Typography>
          )}
          {filterApi.isActive && (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              {filterApi.stats.matching} / {filterApi.stats.total}
            </Typography>
          )}
          {violations.length > 0 && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={`${violations.length} violations`}
              title="תלויות מופרות בסדר הנוכחי"
            />
          )}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, next) => {
              if (next) {
                setMode(next as 'reorder' | 'deps')
              }
            }}
          >
            <ToggleButton value="reorder" sx={{ textTransform: 'none', px: 1.25 }}>
              Reorder
            </ToggleButton>
            <ToggleButton value="deps" sx={{ textTransform: 'none', px: 1.25 }}>
              <LinkIcon fontSize="small" sx={{ mr: 0.5 }} />
              Dependencies
            </ToggleButton>
          </ToggleButtonGroup>
          {tagRegistry && (
            <IconButton
              size="small"
              onClick={(ev) => setFilterAnchor(ev.currentTarget)}
              title="סנן לפי דירוג"
              sx={{ color: filterApi.isActive ? 'primary.main' : 'text.secondary' }}
            >
              <Badge
                badgeContent={filterApi.count}
                color="primary"
                overlap="circular"
                variant="dot"
                invisible={!filterApi.isActive}
              >
                <FilterList fontSize="small" />
              </Badge>
            </IconButton>
          )}
          <IconButton size="small" onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)">
            <Undo fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 0,
            direction: bookDirection,
            overflow: { xs: 'auto', md: 'hidden' },
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={detectCollisions}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
          >
            {/* Chapters area */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexWrap: 'wrap',
                alignContent: 'flex-start',
                gap: { xs: 1, md: 2 },
                p: { xs: 1, md: 2 },
                overflowY: { xs: 'visible', md: 'auto' },
              }}
            >
              <SortableContext items={draftChapters.map((ch) => ch.id)} strategy={rectSortingStrategy}>
                {draftChapters.map((ch) => (
                  <SortableChapter
                    key={ch.id}
                    chapterId={ch.id}
                    disabled={mode === 'deps' || !multipleChapters}
                    renderHeader={
                      multipleChapters
                        ? (dragHandleProps) => (
                            <OrganizeChapterRow
                              title={ch.title}
                              dragHandleProps={mode === 'deps' ? undefined : dragHandleProps}
                              fontFamily={bookFontFamily}
                            />
                          )
                        : undefined
                    }
                  >
                    <DroppableContainer
                      id={chapterZoneId(ch.id)}
                      isEmpty={ch.chapterSections.length === 0}
                      emptyHint="Drag sections here"
                    >
                      <SortableContext items={ch.chapterSections} strategy={verticalListSortingStrategy}>
                        {renderSectionList(ch.chapterSections)}
                      </SortableContext>
                    </DroppableContainer>
                  </SortableChapter>
                ))}
              </SortableContext>
            </Box>

            {/* Unattached sections column */}
            {draftChapters.length > 0 && (
              <Box
                sx={{
                  width: { xs: '100%', md: 280 },
                  flexShrink: 0,
                  borderInlineStart: 'none',
                  borderTop: { xs: '1px solid', md: 'none' },
                  borderColor: 'divider',
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  overflowY: { xs: 'visible', md: 'auto' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    px: 1,
                    pb: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    mb: 0.5,
                  }}
                >
                  Unattached
                </Typography>

                <DroppableContainer id={UNATTACHED_ID} fill>
                  <SortableContext items={draftUnattached} strategy={verticalListSortingStrategy}>
                    {renderSectionList(draftUnattached)}
                  </SortableContext>
                </DroppableContainer>

                {draftUnattached.length === 0 && !activeDragId && (
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: 'text.disabled',
                      textAlign: 'center',
                      py: 2,
                      fontStyle: 'italic',
                    }}
                  >
                    Drag sections here to unattach
                  </Typography>
                )}
              </Box>
            )}

            {/* Drag overlay — the floating preview */}
            <DragOverlay>
              {activeChapter ? (
                <Box
                  sx={{
                    opacity: 0.9,
                    pointerEvents: 'none',
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    p: 1,
                    bgcolor: 'background.paper',
                    minWidth: 240,
                  }}
                >
                  <OrganizeChapterRow title={activeChapter.title} fontFamily={bookFontFamily} />
                </Box>
              ) : activeDragEntry ? (
                <Box sx={{ position: 'relative', opacity: 0.9, pointerEvents: 'none' }}>
                  <OrganizeSectionRow
                    displayText={activeDragEntry.displayText}
                    ancestorPath={[activeDragEntry.chapter.title, ...activeDragEntry.ancestorLabels]
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .join(' \u00b7 ')}
                    isSelected
                    bookFontFamily={bookFontFamily}
                    bookDirection={bookDirection}
                  />
                  {dragCount > 1 && (
                    <Chip
                      label={dragCount}
                      size="small"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        insetInlineEnd: -8,
                        minWidth: 24,
                        height: 24,
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
              ) : null}
            </DragOverlay>
          </DndContext>
        </DialogContent>

        {orphanedDeps.length > 0 && (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1,
              maxHeight: 160,
              overflowY: 'auto',
              direction: bookDirection,
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'warning.main', mb: 0.5 }}>
              תלויות יתומות ({orphanedDeps.length}) — צד אחד נמחק
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {orphanedDeps.map((dep) => (
                <Box
                  key={dep.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography sx={{ flex: 1, fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                    {labelByKey(dep.fromKey)} {bookDirection === 'rtl' ? '←' : '→'} {labelByKey(dep.toKey)}
                    {dep.reason ? ` · ${dep.reason}` : ''}
                  </Typography>
                  <Button size="small" color="inherit" onClick={() => removeDep(dep.id)}>
                    מחק
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={!dirty}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {pendingDepTarget && (
        <AddDependencyDialog
          open
          bookDirection={bookDirection}
          bookFontFamily={bookFontFamily}
          sections={depSectionOptions}
          existingDeps={draftDeps}
          orderedKeys={flatOrder}
          initialFromKey={pendingDepTarget.fromKey}
          initialToKey={pendingDepTarget.toKey}
          initialReason={pendingDepTarget.initialReason}
          initialKind={pendingDepTarget.initialKind}
          onSave={handleDepDialogSave}
          onDelete={handleDepDialogDelete}
          onCancel={() => setPendingDepTarget(null)}
        />
      )}

      {/* Section preview — floats above the organize dialog */}
      <Dialog
        open={previewOpen}
        onClose={closePreview}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'min(72vw, 920px)',
            height: 'min(78vh, 760px)',
            bgcolor: 'var(--bg-surface)',
            direction: bookDirection,
          },
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <SearchPreviewPanel
            previewIdentity={previewKey}
            previewTitle={previewTitle}
            previewAst={previewAst}
            previewLoading={previewLoading}
            previewError={previewError}
            book={book ?? null}
            editable={!previewError && !previewLoading && Boolean(previewAst)}
            onChange={onPreviewChange}
            onSave={onPreviewSave}
            saving={previewSaving}
            hasUnsavedChanges={previewDirty}
            recentlySaved={previewRecentlySaved}
          />
        </Box>
      </Dialog>

      <Popover
        open={filterAnchor !== null}
        onClose={() => setFilterAnchor(null)}
        anchorEl={filterAnchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: bookDirection === 'rtl' ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: bookDirection === 'rtl' ? 'right' : 'left',
        }}
        slotProps={{
          paper: {
            dir: bookDirection,
            elevation: 8,
            sx: {
              p: 1.5,
              minWidth: 300,
              maxWidth: 340,
              borderRadius: 2,
              border: '1px solid var(--border-subtle)',
              backgroundImage: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            },
          },
        }}
      >
        {tagRegistry && <SectionRatingFilterPanel api={filterApi} tagRegistry={tagRegistry} />}
      </Popover>

      <Popover
        open={tagPopover !== null}
        onClose={() => setTagPopover(null)}
        anchorReference="anchorPosition"
        anchorPosition={tagPopover ? { top: tagPopover.top, left: tagPopover.left } : undefined}
        anchorOrigin={{
          vertical: 'top',
          horizontal: bookDirection === 'rtl' ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: bookDirection === 'rtl' ? 'right' : 'left',
        }}
        slotProps={{
          paper: {
            dir: bookDirection,
            elevation: 8,
            sx: {
              p: 1.75,
              minWidth: 280,
              maxWidth: 340,
              borderRadius: 2,
              border: '1px solid var(--border-subtle)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        {tagPopover && tagRegistry && (
          <SectionTagEditor
            tagIds={effectiveTagIds(tagPopover.key)}
            tagRegistry={tagRegistry}
            tagColorMap={tagColorMap}
            layout="compact"
            onChange={(next) => {
              void changeTagIds(tagPopover.key, next).catch((err) => {
                logger.warn('Failed to persist section tags from dialog', err)
              })
            }}
            onOpenTagPicker={() => {
              setPickerSectionKey(tagPopover.key)
              setTagPopover(null)
            }}
          />
        )}
      </Popover>

      {bookId && (
        <TagManagerDialog
          open={pickerSectionKey !== null}
          onClose={() => setPickerSectionKey(null)}
          bookId={bookId}
          direction={bookDirection}
          mode="select"
          onSelectAndSave={(tagId) => {
            if (pickerSectionKey === null) {
              return
            }
            const current = effectiveTagIds(pickerSectionKey)
            if (!current.includes(tagId)) {
              void changeTagIds(pickerSectionKey, [...current, tagId]).catch((err) => {
                logger.warn('Failed to add section tag from picker', err)
              })
            }
          }}
          onRegistrySaved={onRegistrySaved}
        />
      )}
    </>
  )
}
